import React from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import SceneAudioService from "../../../services/sceneAudioService";

const NarrationTextsPanel = (props) => {
  const { baseUrl, trainingId, navItems, navItemsIds, navItemSelected } = props;

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [copySuccess, setCopySuccess] = React.useState(false);

  const skeletonStyles = (
    <style>{`
      @keyframes narration-texts-shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
    `}</style>
  );
  const skeletonWidths = ["100%", "92%", "82%"];

  React.useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const json = await SceneAudioService.getSceneAudios({ baseUrl, trainingId });
        if (!cancelled) {
          setData(json);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Request failed");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [baseUrl, trainingId]);

  const scenes = data && Array.isArray(data.scenes) ? data.scenes : [];

  const hasValidTexts = scenes.some(
    (scene) =>
      scene.audios &&
      scene.audios.some((audio) => audio.narrationText && audio.narrationText.trim() !== "")
  );

  const renderSceneTitle = (scene) => {
    const nav = navItems && navItems[scene.navId];
    if (nav && nav.name) {
      return nav.name;
    }
    return scene.sceneTitle || scene.navId;
  };

  const copyAllTexts = () => {
    let allTexts = "";
    scenes.forEach((scene) => {
      const validAudios =
        scene.audios && scene.audios.length > 0
          ? scene.audios.filter((audio) => audio.narrationText && audio.narrationText.trim() !== "")
          : [];

      if (validAudios.length === 0) {
        return;
      }

      const sceneTitle = renderSceneTitle(scene);
      allTexts += `${sceneTitle}\n`;
      validAudios.forEach((audio) => {
        allTexts += `${audio.narrationText}\n\n`;
      });
      allTexts += "\n";
    });

    navigator.clipboard.writeText(allTexts).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxSizing: "border-box",
        gap: 8,
        paddingBottom: 100,
      }}
    >
      {skeletonStyles}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "14px",
          fontWeight: 600,
          color: "#0f172a",
          marginBottom: 4,
          padding: "12px",
        }}
      >
        <span>{i18n.t("AudioPanel.NarrationTexts")}</span>
        {!loading && !error && hasValidTexts && (
          <button
            type="button"
            onClick={copyAllTexts}
            style={{
              fontSize: 11,
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #E5E7EB",
              backgroundColor: copySuccess ? "#10B981" : "#F9FAFB",
              color: copySuccess ? "#FFFFFF" : "#6B7280",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            title={i18n.t("AudioPanel.CopyAll")}
          >
            {copySuccess ? i18n.t("AudioPanel.Copied") : i18n.t("AudioPanel.CopyAll")}
          </button>
        )}
      </div>
      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "0 12px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {skeletonWidths.map((width, idx) => (
              <div
                key={idx}
                style={{
                  height: 56,
                  borderRadius: 10,
                  background: "linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
                  backgroundSize: "200% 100%",
                  animation: "narration-texts-shimmer 1.1s ease-in-out infinite",
                  width,
                }}
              />
            ))}
          </div>
        </div>
      ) : error ? (
        <div style={{ fontSize: 12, color: "#b91c1c", padding: "0 12px" }}>
          {i18n.t("AudioPanel.ErrorPrefix")}: {error}
        </div>
      ) : !hasValidTexts ? (
        <div style={{ fontSize: 12, color: "#6b7280", padding: "0 12px" }}>
          {i18n.t("AudioPanel.NoNarrationTexts")}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            overflowY: "auto",
            padding: "0 12px",
          }}
          className="dndWindow"
        >
          {scenes.map((scene) => {
            const validAudios =
              scene.audios && scene.audios.length > 0
                ? scene.audios.filter(
                    (audio) => audio.narrationText && audio.narrationText.trim() !== ""
                  )
                : [];

            if (validAudios.length === 0) {
              return null;
            }

            return (
              <div
                key={scene.navId}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#111827",
                    userSelect: "text",
                  }}
                >
                  {renderSceneTitle(scene)}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    paddingLeft: 8,
                  }}
                >
                  {validAudios.map((audio) => {
                    return (
                      <div
                        key={audio.fileId}
                        style={{
                          fontSize: 13,
                          color: "#374151",
                          lineHeight: "1.6",
                          userSelect: "text",
                          WebkitUserSelect: "text",
                          MozUserSelect: "text",
                          msUserSelect: "text",
                        }}
                      >
                        {audio.narrationText}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

NarrationTextsPanel.propTypes = {
  baseUrl: PropTypes.string,
  trainingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  navItems: PropTypes.object,
  navItemsIds: PropTypes.array,
  navItemSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default NarrationTextsPanel;
